import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  IconButton,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';
import {
  Column,
  SimpleTable
} from '../../../../components/SimpleTable';
import {SupportTicketErrorTypeEnum} from '../../../../model/support/support-ticket-error-type.enum';
import {SupportTicketModuleEnum} from '../../../../model/support/support-ticket-module.enum';
import {SupportTicketResolutionEnum} from '../../../../model/support/support-ticket-resolution.enum';
import {SupportTicketStatusEnum} from '../../../../model/support/support-ticket-status.enum';
import {SupportTicketTypeEnum} from '../../../../model/support/support-ticket-type.enum';
import {SupportTicketModel} from '../../../../model/support/support-ticket.model';
import {SearchableOrder} from '../../../../state/searchable-state';
import {FormatUtils} from '../../../../utils/format.utils';
import {SupportTicketUtils} from '../utils/support-ticket.utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Folder from '@mui/icons-material/Folder';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';

type Props = {
  tickets: SupportTicketModel[]
  translateLocal: (key: string) => string
  translate: (key: string) => string
  onTicketPressed: (ticket: SupportTicketModel) => void
  onTicketRelatedTasksPressed: (ticket: SupportTicketModel) => void
  onTicketAttachmentPressed: (ticket: SupportTicketModel) => void
  ordering: SearchableOrder[];
  onOrdering: (ordering: SearchableOrder) => void;
}

export const SupportTicketTable = ({tickets, onTicketPressed, onTicketRelatedTasksPressed, onTicketAttachmentPressed, ordering, onOrdering, translateLocal, translate}: Props) => {
  const translateModule = (module: SupportTicketModuleEnum) => translate(`support.ticket.module.${module}`)
  const translateStatus = (status: SupportTicketStatusEnum) => translate(`support.ticket.status.${status}`)
  const translateType = (type: SupportTicketTypeEnum) => translate(`support.ticket.type.${type}`)
  const translateErrorType = (type: SupportTicketErrorTypeEnum | null) => type ? translate(`support.ticket.error_type.${type}`) : ''

  const buildStatus = (status: SupportTicketStatusEnum) => {
    const Icon = SupportTicketUtils.getStatusIcon(status)

    return <Box display="flex" alignItems="center" gap={1}>
      <Icon style={{ color: SupportTicketUtils.getStatusIconColor(status) }} />
      {translateStatus(status)}
    </Box>
  }

  const buildResolution = (resolution: SupportTicketResolutionEnum) => {
    const resolutionGeneral = resolution === 'FIX_SCHEDULED'
      ? 'ACCEPTED'
      : (resolution === 'REJECTED_BY_CUSTOMER' ? 'REJECTED_BY_CUSTOMER' : 'REJECTED')

    return <Box display="flex" alignItems="center" gap={1}>
        {
          resolutionGeneral === 'ACCEPTED'
            ? <CheckCircleIcon sx={{ color: 'green', pr: 0.5 }} fontSize="small" />
            : <ErrorOutlineIcon sx={{ color: '#FF7074', pr: 0.5 }} fontSize="small" />
        }
        {translate(`support.ticket.resolution.general.${resolutionGeneral}`)}
    </Box>
  }

  const columns: Column<SupportTicketModel>[] = [
    { key: 'id', orderKey: 'support_ticket.id', label: translateLocal('column.id'), render: (row) => {
        return <Box key={row.id} display="flex" alignItems="center" gap={0.5}>
          <IconButton onClick={() => onTicketPressed(row)} aria-label={translate('common.details.title')}>
            <InfoOutlinedIcon />
          </IconButton>
          {
            row.relatedTasks?.length > 0
              ? <IconButton onClick={() => onTicketRelatedTasksPressed(row)} aria-label={translate('common.details.title')}>
                <LinkIcon />
              </IconButton> : <></>
          }
          <IconButton onClick={() => onTicketAttachmentPressed(row)} aria-label={translate('common.details.title')}>
            <Folder />
          </IconButton>
          <Typography variant="body2">{row.id}</Typography>
        </Box>
    } },
    { key: 'module', label: translateLocal('column.module'), render: (row) => translateModule(row.module) },
    { key: 'type', label: translateLocal('column.type'), render: (row) => translateType(row.type) },
    { key: 'errorType', label: translateLocal('column.error_type'), render: (row) => translateErrorType(row.errorType) },
    { key: 'priority', orderKey: 'support_ticket.priority', label: translateLocal('column.priority'), render: (row) => SupportTicketUtils.ticketPriorityOptions.find(it => it.value === row.priority)?.label ?? '-' },
    { key: 'title', label: translateLocal('column.title'), render: (row) => row.title },
    { key: 'organization', orderKey: 'support_ticket.organization_name', label: translateLocal('column.organization'), render: (row) => row.organizationName },
    { key: 'account', orderKey: 'support_ticket.account_name', label: translateLocal('column.account'), render: (row) => row.accountName },
    { key: 'status', orderKey: 'support_ticket.status', label: translateLocal('column.status'), render: (row) => buildStatus(row.status) },
    { key: 'resolution', orderKey: 'support_ticket.resolution',label: translateLocal('column.resolution'), render: (row) => row.resolution ? buildResolution(row.resolution) : '' },
    { key: 'estimatedFixDate', orderKey: 'support_ticket.estimated_fix_date', label: translateLocal('column.estimatedFixDate'), render: (row) => row.estimatedFixDate ? FormatUtils.formatDate(row.estimatedFixDate) : ''},
    { key: 'createDate', orderKey: 'support_ticket.created_date_timestamp', label: translateLocal('column.createDate'), render: (row) => FormatUtils.formatDate(row.createDate) },
  ]

  return <SimpleTable
    rows={tickets}
    columns={columns}
    rowKey={(row) => row.id}
    ordering={ordering}
    onOrdering={onOrdering}
  />
}